import xstfLogo from "../assets/images/xstf.png";
import criticalLogo from "../assets/images/critical_software_logo.jpeg";
import pwcLogo from "../assets/images/images.png";
import logoImg from "../assets/images/lm.png";
import uaLogo from "../assets/images/f9Y_g782_400x400.jpg";
import fcupLogo from "../assets/images/UGsfPdgJ_400x400.jpg";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faGraduationCap, faUsers } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { motion, useScroll, useTransform } from "framer-motion";
import "../assets/styles/Timeline.scss";

type TimelineEntry = {
  date: string;
  title: string;
  org: string;
  logo: string;
  icon: IconDefinition;
  kind: "work" | "education";
  description: string;
};

const entries: TimelineEntry[] = [
  {
    date: "2021 – 2024",
    title: "BSc in Computer and Informatics Engineering",
    org: "University of Aveiro",
    logo: uaLogo,
    icon: faGraduationCap,
    kind: "education",
    description:
      "Algorithms & data structures, computer architecture, operating systems, communications networks, databases, AI, and informatics & communications security.",
  },
  {
    date: "06/2024 – 11/2024",
    title: "SAP & Cloud Trainee (Internship)",
    org: "Lynxmind",
    logo: logoImg,
    icon: faBriefcase,
    kind: "work",
    description:
      "Deployed secure SAP on AWS (IAM roles, encryption, segmentation, monitoring). Automated checks & log analysis with Bash/Python.",
  },
  {
    date: "2024 – 2026",
    title: "MSc in Cybersecurity",
    org: "FCUP - University of Porto",
    logo: fcupLogo,
    icon: faGraduationCap,
    kind: "education",
    description:
      "Pentesting simulations, malware sandboxing, fuzzing, secure software engineering, distributed systems, secure CI/CD.",
  },
  {
    date: "09/2024 – Present",
    title: "Co-captain",
    org: "xSTF - University of Porto",
    logo: xstfLogo,
    icon: faUsers,
    kind: "work",
    description: "Advanced challenges in cloud, misc, OSINT, forensics and reverse engineering.",
  },
  {
    date: "09/2025 – 02/2026",
    title: "Cybersecurity Engineer",
    org: "Critical Software",
    logo: criticalLogo,
    icon: faBriefcase,
    kind: "work",
    description: "Vulnerability triage, threat modeling, and risk/compliance assessments for critical systems.",
  },
  {
    date: "09/2026 – Present",
    title: "Cybersecurity Analyst",
    org: "PwC Portugal",
    logo: pwcLogo,
    icon: faBriefcase,
    kind: "work",
    description:
      "IT risk assessments, control testing, and cybersecurity compliance reviews, helping organizations improve security posture and regulatory compliance.",
  },
];

// Gap from the centre line to the near edge of a card: dot radius + stem.
// Must match $dot-half + $stem-length in Timeline.scss.
const CARD_GAP = 40;
// Breathing room between a card's far edge and the edge of the pinned area.
const GUTTER = 4;

function Timeline() {
  const containerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const distanceRef = useRef(0);

  const [distance, setDistance] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [lineY, setLineY] = useState(0);
  const [stickyHeight, setStickyHeight] = useState(0);
  const [isHorizontal, setIsHorizontal] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, (value) => -value * distanceRef.current);

  const measure = useCallback(() => {
    const horizontal = window.matchMedia("(min-width: 900px)").matches;
    setIsHorizontal(horizontal);

    const viewport = viewportRef.current;
    const track = trackRef.current;
    const heading = headingRef.current;
    if (!horizontal || !viewport || !track || !heading) {
      distanceRef.current = 0;
      setDistance(0);
      setViewportHeight(0);
      setLineY(0);
      setStickyHeight(0);
      x.set(0);
      return;
    }

    // Size each side of the line to the tallest card actually on that side, so
    // neither half reserves space it never uses.
    let tallestAbove = 0;
    let tallestBelow = 0;
    track.querySelectorAll<HTMLElement>(".htimeline__item").forEach((item) => {
      const card = item.querySelector<HTMLElement>(".htimeline__card");
      if (!card) return;
      if (item.classList.contains("htimeline__item--above")) {
        tallestAbove = Math.max(tallestAbove, card.offsetHeight);
      } else {
        tallestBelow = Math.max(tallestBelow, card.offsetHeight);
      }
    });

    const headingH = heading.offsetHeight;
    const available = window.innerHeight - headingH;

    let nextLineY = GUTTER + tallestAbove + CARD_GAP;
    let nextViewportH = nextLineY + CARD_GAP + tallestBelow + GUTTER;
    if (nextViewportH > available) {
      // Shrink both halves proportionally rather than clipping one.
      const scale = available / nextViewportH;
      nextLineY = Math.round(nextLineY * scale);
      nextViewportH = available;
    }

    setLineY(nextLineY);
    setViewportHeight(nextViewportH);
    setStickyHeight(headingH + nextViewportH);

    const next = Math.max(0, track.scrollWidth - viewport.clientWidth);
    distanceRef.current = next;
    setDistance(next);
    // Re-evaluate the transform immediately: the ref changed without a scroll event.
    x.set(-scrollYProgress.get() * next);
  }, [scrollYProgress, x]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // Scroll length: the pinned area, plus the horizontal distance to cover.
  const containerHeight = isHorizontal ? `${stickyHeight + distance}px` : "auto";

  return (
    <div id="history">
      <div className="htimeline" ref={containerRef} style={{ height: containerHeight }}>
        <div className="htimeline__sticky">
          {/* Heading lives inside the pinned area so it stays with the track. */}
          <div className="projects-container htimeline__heading" ref={headingRef}>
            <h1>Career History</h1>
          </div>

          <div
            className="htimeline__viewport"
            ref={viewportRef}
            style={
              isHorizontal && viewportHeight
                ? ({ height: viewportHeight, "--line-y": `${lineY}px` } as React.CSSProperties)
                : undefined
            }
          >
            <div className="htimeline__line" />
            <motion.div className="htimeline__track" ref={trackRef} style={{ x }}>
              {entries.map((entry, index) => (
                <div
                  key={entry.title + entry.date}
                  className={`htimeline__item htimeline__item--${index % 2 === 0 ? "below" : "above"}`}
                >
                  <span className="htimeline__date">{entry.date}</span>
                  <div className={`htimeline__card htimeline__card--${entry.kind}`}>
                    <h3 className="htimeline__title">{entry.title}</h3>
                    <div className="htimeline__org">
                      <span className="logo-box">
                        <img src={entry.logo} alt={`${entry.org} logo`} />
                      </span>
                      <h4>{entry.org}</h4>
                    </div>
                    <p>{entry.description}</p>
                  </div>
                  <span className="htimeline__stem" />
                  <span className="htimeline__dot">
                    <FontAwesomeIcon icon={entry.icon} />
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Timeline;
